import React, { useMemo } from "react";
import { htmlToPlainText, looksLikeHtml, normalizeKenticoHref, sanitizeHtml } from "../utils/safeHtml";
import { maxPlatform } from "../services/maxPlatform";

type RichTextProps = {
  html: string | null | undefined;
  className?: string;
  /** When true, strip tags and show plain text (cards / previews). */
  plain?: boolean;
};

function isOpenableHref(href: string): boolean {
  const v = href.trim().toLowerCase();
  return (
    v.startsWith("http:") ||
    v.startsWith("https:") ||
    v.startsWith("mailto:") ||
    v.startsWith("tel:")
  );
}

/**
 * Renders CMS/Tezam HTML descriptions safely. Plain text passes through unchanged.
 * Embedded <a> open via maxPlatform (MAX WebView-safe); Kentico ~/host/… → https.
 */
export function RichText({ html, className, plain = false }: RichTextProps) {
  const value = html?.trim() ?? "";
  const isHtml = looksLikeHtml(value);

  const safe = useMemo(() => {
    if (!value) return "";
    if (plain || !isHtml) return plain && isHtml ? htmlToPlainText(value) : value;
    return sanitizeHtml(value);
  }, [value, plain, isHtml]);

  if (!safe) return null;

  if (plain || !isHtml) {
    return <p className={className}>{safe}</p>;
  }

  const onClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement | null;
    const anchor = target?.closest?.("a") as HTMLAnchorElement | null;
    if (!anchor) return;
    const href = normalizeKenticoHref(anchor.getAttribute("href") || "");
    if (!href || !isOpenableHref(href)) return;
    e.preventDefault();
    e.stopPropagation();
    maxPlatform.openExternalLink(href);
  };

  return (
    <div
      className={`rich-text [&_p]:mb-2 [&_p:last-child]:mb-0 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mb-1 [&_a]:text-indigo-600 [&_a]:underline [&_a]:cursor-pointer [&_strong]:font-semibold [&_em]:italic ${className ?? ""}`}
      dangerouslySetInnerHTML={{ __html: safe }}
      onClick={onClick}
    />
  );
}
